import React from "react";
import "./signInScreen.css";
import { WirderMark } from "./components/wirderMark";
import { AppleMark } from "./components/icons";
import { googleAuth } from "../auth/googleAuth";
import { appleAuth, isNativeIos } from "../auth/appleAuth";
import { ConnectingScreen } from "./connectingScreen";
import { hideNativeSplash } from "./components/nativeSplash";

export function SignInScreen() {
  const authContainerRef = React.useRef<HTMLDivElement>(null);
  const buttonContainerRef = React.useRef<HTMLDivElement>(null);
  const [renderedGoogleButton, setRenderedGoogleButton] = React.useState(false);
  const [appleError, setAppleError] = React.useState<string | undefined>();
  const native = isNativeIos();
  const [nativeSigningIn, setNativeSigningIn] = React.useState(native);

  // Signed-out native users land here (possibly after a failed silent sign-in),
  // so make sure the native splash is dismissed even if we never route through
  // ConnectingScreen. Idempotent + no-op on web.
  React.useEffect(() => {
    hideNativeSplash();
  }, []);

  React.useEffect(() => {
    if (native) return; // Google JS SDK is not used on native iOS.
    if (buttonContainerRef.current && !renderedGoogleButton) {
      setRenderedGoogleButton(true);
      // Match the Google button width to our button column (GIS clamps 200–400).
      const measured = authContainerRef.current?.clientWidth ?? 320;
      const width = Math.max(200, Math.min(400, measured));
      googleAuth.renderSignInButton(buttonContainerRef.current, { width }).catch((err) => {
        console.error("Failed to render Google sign-in button:", err);
      });
    }
  }, [renderedGoogleButton, native]);

  const onAppleSignIn = React.useCallback(() => {
    setAppleError(undefined);
    if (native) setNativeSigningIn(true);
    appleAuth.signIn().catch((err: unknown) => {
      console.error("Apple sign-in failed:", err);
      if (native) setNativeSigningIn(false);
      setAppleError(err instanceof Error ? err.message : "Apple sign-in failed. Please try again.");
    });
  }, [native]);

  // On native iOS, auto-trigger Apple sign-in on first mount so the user
  // sees the Face ID sheet immediately without an extra tap. We still render
  // the button below as a fallback in case the user dismisses the sheet.
  const autoTriggeredRef = React.useRef(false);
  React.useEffect(() => {
    if (native && !autoTriggeredRef.current) {
      autoTriggeredRef.current = true;
      onAppleSignIn();
    }
  }, [native, onAppleSignIn]);

  // On native iOS a returning player is silently re-authenticated on launch.
  // Show the connecting animation instead of the sign-in hero so they don't
  // flash the login screen on the way to Home.
  if (native && nativeSigningIn) {
    return <ConnectingScreen mode="static" caption={"Signing you in\u2026"} />;
  }

  return (
    <div className="screen signin">
      <div className="signin__hero">
        <WirderMark className="signin__logo" />
        <div className="signin__name">
          <div className="display signin__word">Wirder</div>
          <div className="signin__defn">
            <span className="signin__defn-head">
              Wirder <span className="pron">(wer&middot;der)</span> <span className="pos">n.</span>
            </span>
            <span className="signin__defn-body">A bird with a passion for finding rare words.</span>
          </div>
        </div>
        <h1 className="signin__tag">Find what others miss.</h1>
        <p className="signin__pitch">
          A daily word hunt where the rarest finds score the most. How many can you spot before the
          clock runs out?
        </p>
      </div>

      <div className="signin__auth" ref={authContainerRef}>
        <p className="signin__microcopy">
          <span className="signin__free">Free to play!</span>
          <span>Sign in so we can save your scores</span>
        </p>

        {nativeSigningIn ? (
          <p className="signin__signing">Signing in&hellip;</p>
        ) : (
          <>
            <button className="btn btn--apple" onClick={onAppleSignIn}>
              <AppleMark />
              Continue with Apple
            </button>
            {!native && <div className="signin__google" ref={buttonContainerRef}></div>}
          </>
        )}

        {appleError && <div className="signin__error">{appleError}</div>}

        <p className="signin__foot">
          We respect your privacy.{" "}
          <a
            className="signin__link"
            href="/privacy.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Read more
          </a>
        </p>
      </div>
    </div>
  );
}
